import sessionHandling from "services/utils/notificationUtils";
import { setCachedPayInputConfig } from "pages/PayRoll/Configuration/PayInputConfiguration/payInputConfigApi";

// API calls for Payroll Settings > Bonus (Cauver Bonus spec v1.6, Bonus API
// doc of 3 Nov 2026). Base: /api/payroll/configuration/pay-input/bonus/
//   settings/                      GET / PUT
//   types/                         GET (paged) / POST
//   types/{id}/                    GET / PATCH / DELETE
//   types/{id}/versions/           GET
//   types/check-formula/           POST
//   formula-variables/, wage-components/, pay-components/   GET
// The Pay Inputs > Bonus work screen (runs, entries, import) is a separate
// API that the backend has not built yet.

export const BONUS_PAY_INPUT_TYPE = "BONUS";
const BASE = "payroll/configuration/pay-input/bonus/";

export class BonusApiError extends Error {
  constructor(message, { status, code, fieldErrors } = {}) {
    super(message);
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors || {};
  }
}

const textOf = (value) => {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(textOf).filter(Boolean).join(" ");
  if (typeof value === "object") return Object.values(value).map(textOf).filter(Boolean).join(" ");
  return String(value);
};

// Errors come as { code, field, message, errors: [{ code, field, message,
// meta }] }. Several field errors arrive together; a formula error carries
// meta.position (1-based).
const withPosition = (item) =>
  item?.meta?.position ? `${item.message} (at character ${item.meta.position})` : item?.message;

const parseError = (body, status) => {
  const fieldErrors = {};
  let message = "";
  let code = body?.code;
  if (Array.isArray(body?.errors) && body.errors.length) {
    const general = [];
    body.errors.forEach((item) => {
      code = code || item?.code;
      const text = withPosition(item);
      if (item?.field) {
        fieldErrors[item.field] = fieldErrors[item.field] ? `${fieldErrors[item.field]} ${text}` : text;
      } else if (text) {
        general.push(text);
      }
    });
    message = general.join(" ") || (Object.keys(fieldErrors).length ? "Check the highlighted fields." : "");
  } else if (body?.message || body?.error || body?.detail) {
    message = textOf(body.message || body.error || body.detail);
  } else if (body && typeof body === "object") {
    Object.entries(body).forEach(([field, value]) => { fieldErrors[field] = textOf(value); });
    message = Object.values(fieldErrors).join(" ");
  }
  return new BonusApiError(message || `Request failed (${status}). Please try again.`, { status, code, fieldErrors });
};

const buildUrl = (domain, path, query) => {
  const params = new URLSearchParams();
  Object.entries(query || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") params.append(key, value);
  });
  const search = params.toString();
  return `${String(domain || "").replace(/\/$/, "")}/${BASE}${path}${search ? `?${search}` : ""}`;
};

const request = async (domain, path, { method = "GET", query, body, signal } = {}) => {
  const headers = { Authorization: `Token ${localStorage.getItem("token")}` };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const response = await fetch(buildUrl(domain, path, query), {
    method,
    signal,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (response.status === 401) {
    localStorage.clear();
    window.location.href = "/auth/login";
    throw new BonusApiError("Your session has expired. Please sign in again.", { status: 401 });
  }
  if (response.status === 204) return null;
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = parseError(data, response.status);
    // 409 is "logged in elsewhere" on this server, except TYPE_IN_USE on delete.
    if (response.status === 409 && error.code !== "TYPE_IN_USE") sessionHandling();
    throw error;
  }
  return data;
};

const listOf = (data) => (Array.isArray(data) ? data : data?.results || []);

// ---------- Settings ----------

// Spec wording for the sources (the API's labels are shorter).
const SOURCE_LABELS = {
  BONUS_RUN: "Bonus run — calculated by Cauver",
  MANUAL: "Manual input",
  IMPORT: "Import",
  ERP: "Integration / API",
};

// Same shape as the shared pay-input config (payInputConfigApi.fromApiConfig)
// so the Pay Input settings screen can use it, plus rounding. Approval is
// fixed at No in V1 (spec B2): shown, with Yes disabled.
export const fromApiBonusSettings = (data) => ({
  is_configured: Boolean(data.is_configured),
  enabled: data.is_enabled !== false,
  treatment: data.default_treatment,
  treatmentOptions: data.treatment_options || [],
  approvalRequired: "no",
  approvalConfigurable: false,
  approvalLocked: true,
  rounding: data.rounding,
  roundingOptions: data.rounding_options || [],
  sourceOptions: (data.source_details || []).map((source) => ({
    value: source.source_type,
    label: SOURCE_LABELS[source.source_type] || source.label,
    comingSoon: !source.is_available || source.source_type === "ERP",
  })),
  sources: (data.source_details || [])
    .filter((source) => source.is_available && source.source_type !== "ERP" && data.allowed_sources?.[source.source_type])
    .map((source) => source.source_type),
});

// GET without `config`, PUT with it (same call shape as requestPayInputConfig).
// is_enabled is not on the screen (not in spec B2), so it is not sent and
// keeps its value.
export const requestBonusSettings = async ({ domain, companyId, config, signal }) => {
  if (!config) {
    const data = await request(domain, "settings/", { query: { company_id: companyId }, signal });
    const loaded = fromApiBonusSettings(data || {});
    setCachedPayInputConfig(domain, companyId, BONUS_PAY_INPUT_TYPE, loaded);
    return { config: loaded };
  }
  const result = await request(domain, "settings/", {
    method: "PUT",
    signal,
    body: {
      company_id: Number(companyId),
      default_treatment: config.treatment,
      rounding: config.rounding,
      approval_required: false,
      allowed_sources: Object.fromEntries(config.sourceOptions.map(({ value, comingSoon }) => [
        value, !comingSoon && value !== "ERP" && config.sources.includes(value),
      ])),
    },
  });
  const saved = fromApiBonusSettings(result?.data || {});
  setCachedPayInputConfig(domain, companyId, BONUS_PAY_INPUT_TYPE, saved);
  return { config: saved, message: result?.message };
};

// ---------- Bonus types ----------

// Rows keep the API's field names; the form converts them (bonusTypesData.js).
export const fetchBonusTypes = async ({ domain, companyId, signal }) => {
  const data = await request(domain, "types/", { query: { company_id: companyId, page_size: 200 }, signal });
  return listOf(data);
};

export const createBonusType = async ({ domain, companyId, payload }) => {
  const result = await request(domain, "types/", { method: "POST", body: { company_id: Number(companyId), ...payload } });
  return { type: result?.data, message: result?.message };
};

// Send only what changed. A used type gets a new version for rule changes
// (new_version true); a status-only change never makes a version.
export const updateBonusType = async ({ domain, id, payload }) => {
  const result = await request(domain, `types/${id}/`, { method: "PATCH", body: payload });
  return { type: result?.data, message: result?.message, newVersion: Boolean(result?.new_version) };
};

export const deleteBonusType = ({ domain, id }) => request(domain, `types/${id}/`, { method: "DELETE" });

export const fetchBonusTypeVersions = async ({ domain, id, signal }) => {
  const data = await request(domain, `types/${id}/versions/`, { signal });
  return { currentVersion: data?.current_version, versions: data?.versions || [] };
};

// Always 200: { valid, errors: [{ position, message }], uses }.
export const checkBonusFormula = async ({ domain, companyId, formula, signal }) => {
  const data = await request(domain, "types/check-formula/", {
    method: "POST",
    signal,
    body: { company_id: Number(companyId), formula },
  });
  return {
    valid: Boolean(data?.valid),
    errors: (data?.errors || []).map((item) => (item.position ? `${item.message} (at character ${item.position})` : item.message)),
    uses: data?.uses || [],
  };
};

export const fetchFormulaVariables = async ({ domain, companyId, signal }) => {
  const data = await request(domain, "formula-variables/", { query: { company_id: companyId }, signal });
  return {
    system: data?.system || [],
    components: data?.components || [],
    functions: data?.functions || [],
  };
};

// Gross first, then active Earnings / Other Earnings components. id is a
// number, or "GROSS".
export const fetchWageComponents = async ({ domain, companyId, signal }) => {
  const data = await request(domain, "wage-components/", { query: { company_id: companyId }, signal });
  return listOf(data).map((item) => ({
    value: item.id,
    label: item.name,
    includesOvertime: Boolean(item.includes_overtime),
  }));
};

// Active Other Earnings components no other bonus type uses. bonusTypeId
// (when editing) keeps the type's own line in the list.
export const fetchBonusPayComponents = async ({ domain, companyId, bonusTypeId, signal }) => {
  const data = await request(domain, "pay-components/", {
    query: { company_id: companyId, bonus_type_id: bonusTypeId },
    signal,
  });
  return listOf(data).map((item) => ({ value: item.id, label: item.payslip_name || item.name }));
};
